import { NavLink, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FolderOpen, Library, Sparkles, Plus, MoreHorizontal } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useState, useRef, useLayoutEffect, useCallback } from 'react';
import { MobileMenuSheet } from './MobileMenuSheet';

interface NavTabProps {
  to: string;
  icon: React.ElementType;
  label: string;
  isActive: boolean;
  anchorRef?: React.RefObject<HTMLDivElement>;
}

function NavTab({ to, icon: Icon, label, isActive, anchorRef }: NavTabProps) {
  return (
    <NavLink to={to} className="flex-1 flex flex-col items-center justify-center py-2">
      <div
        ref={anchorRef}
        className={cn(
          'flex flex-col items-center gap-0.5 transition-colors',
          isActive ? 'text-primary' : 'text-muted-foreground'
        )}
      >
        <motion.div whileTap={{ scale: 0.95 }}>
          <Icon className="w-5 h-5" />
        </motion.div>
        <span className="text-[10px] font-medium">{label}</span>
      </div>
    </NavLink>
  );
}

const INDICATOR_WIDTH = 32; // w-8 = 2rem = 32px

export function MobileBottomNav() {
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [indicatorX, setIndicatorX] = useState<number | null>(null);
  
  const containerRef = useRef<HTMLDivElement>(null);
  const projectsRef = useRef<HTMLDivElement>(null);
  const researchRef = useRef<HTMLDivElement>(null);
  const aiRef = useRef<HTMLDivElement>(null);
  
  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const getActiveRef = useCallback(() => {
    if (isActive('/projects')) return projectsRef;
    if (isActive('/research')) return researchRef;
    if (isActive('/ai-assistant')) return aiRef;
    return null;
  }, [location.pathname]);

  const measureAndSetPosition = useCallback(() => {
    const container = containerRef.current;
    const activeRef = getActiveRef();
    const anchor = activeRef?.current;

    if (!container || !anchor) {
      setIndicatorX(null);
      return;
    }

    const containerRect = container.getBoundingClientRect();
    const anchorRect = anchor.getBoundingClientRect();
    const anchorCenter = anchorRect.left + anchorRect.width / 2;
    const x = anchorCenter - containerRect.left - INDICATOR_WIDTH / 2;
    
    setIndicatorX(x);
  }, [getActiveRef]);

  useLayoutEffect(() => {
    // Use double RAF to ensure DOM is fully settled after navigation
    const raf1 = requestAnimationFrame(() => {
      const raf2 = requestAnimationFrame(() => {
        measureAndSetPosition();
      });
      return () => cancelAnimationFrame(raf2);
    });

    return () => cancelAnimationFrame(raf1);
  }, [location.pathname, measureAndSetPosition]);

  // Also measure on resize/orientation change
  useLayoutEffect(() => {
    const handleResize = () => {
      requestAnimationFrame(measureAndSetPosition);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, [measureAndSetPosition]);

  const showIndicator = indicatorX !== null && (isActive('/projects') || isActive('/research') || isActive('/ai-assistant'));

  return (
    <>
      <nav 
        className="fixed bottom-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-lg border-t border-border md:hidden"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        <div ref={containerRef} className="relative flex items-stretch h-14">
          {/* Single persistent indicator */}
          {showIndicator && (
            <motion.div
              className="absolute top-0 h-0.5 bg-primary rounded-full"
              style={{ width: INDICATOR_WIDTH }}
              initial={false}
              animate={{ x: indicatorX }}
              transition={{ type: 'spring', stiffness: 500, damping: 30 }}
            />
          )}

          <NavTab 
            to="/projects" 
            icon={FolderOpen} 
            label="Projects" 
            isActive={isActive('/projects')}
            anchorRef={projectsRef}
          />
          <NavTab 
            to="/research" 
            icon={Library} 
            label="Research" 
            isActive={isActive('/research')}
            anchorRef={researchRef}
          />
          
          {/* Center New Project Button */}
          <div className="flex-1 flex items-center justify-center">
            <NavLink to="/projects/new">
              <motion.button
                whileTap={{ scale: 0.9 }}
                className={cn(
                  'w-12 h-12 rounded-full bg-primary text-primary-foreground shadow-lg flex items-center justify-center',
                  '-mt-4 ring-4 ring-background'
                )}
              >
                <Plus className="w-6 h-6" />
              </motion.button>
            </NavLink>
          </div>
          
          <NavTab 
            to="/ai-assistant" 
            icon={Sparkles} 
            label="AI" 
            isActive={isActive('/ai-assistant')}
            anchorRef={aiRef}
          />
          
          {/* More Menu Button */}
          <button 
            onClick={() => setMenuOpen(true)}
            className="flex-1 flex flex-col items-center justify-center py-2"
          >
            <motion.div
              className="flex flex-col items-center gap-0.5 text-muted-foreground"
              whileTap={{ scale: 0.95 }}
            >
              <MoreHorizontal className="w-5 h-5" />
              <span className="text-[10px] font-medium">More</span>
            </motion.div>
          </button>
        </div>
      </nav>

      <MobileMenuSheet open={menuOpen} onOpenChange={setMenuOpen} />
    </>
  );
}
